import { ref, reactive, computed } from "vue";
import type { TableHeader, ListData, ListOptions } from "../types";
import { MEMBER_LIST_FILE_NAME } from "../constants";
import {
  localStorageGetPageHeaders,
  localStorageSetPageHeaders,
} from "@/common/localStorageUtil";

/**
 * 一覧テーブル表示に必要なデータ整形と列情報を提供する composable。
 *
 * @returns テーブル描画に利用する列定義と表示用データ
 */
export const useMemberTable = () => {
  /**
   * 会員一覧 ヘッダー
   */
  const defaultTableHeaders = ref<TableHeader[]>([
    {
      id: 1,
      display: false,
      title: "会員番号",
      text: "会員番号",
      settingText: "会員番号",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      key: "memberId",
      value: "memberId",
    },
    {
      id: 2,
      display: true,
      title: "お名前",
      text: "お名前",
      settingText: "お名前",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      key: "name",
      value: "name",
    },
    {
      id: 3,
      display: true,
      title: "お名前(かな)",
      text: "お名前(かな)",
      settingText: "お名前(かな)",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      key: "name_kana",
      value: "name_kana",
    },
    {
      id: 4,
      display: true,
      title: "郵便番号",
      text: "郵便番号",
      settingText: "郵便番号",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      key: "zip_code",
      value: "zip_code",
    },
    {
      id: 5,
      display: true,
      title: "住所",
      text: "住所",
      settingText: "住所",
      align: "left",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      key: "address",
      value: "address",
    },
    {
      id: 6,
      display: true,
      title: "電話番号",
      text: "電話番号",
      settingText: "電話番号",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      key: "phone",
      value: "phone",
    },
    {
      id: 8,
      display: true,
      title: "",
      text: "",
      settingText: "(操作)",
      align: "center",
      sortable: false,
      orderDisabled: true,
      order: "",
      orderKey: "",
      key: "actions",
      value: "actions",
    },
  ]);

  /**
   * 会員一覧 ヘッダー
   */
  const tableHeaders = ref<TableHeader[]>([]);

  /**
   * 会員一覧 ヘッダー設定用
   */
  const tableHeadersSetting = ref<TableHeader[]>([]);

  /**
   * 会員一覧 ヘッダー を初期化
   */
  const initTableHeaders = (
    wordList: Record<string, string>,
    house_card_display: number
  ): void => {
    //一覧のヘッダ修正
    if (house_card_display == 1) {
      defaultTableHeaders.value.push({
        id: 7,
        display: true,
        title: wordList["lbl_house_card"],
        text: wordList["lbl_house_card"],
        settingText: wordList["lbl_house_card"],
        align: "end",
        sortable: false,
        orderDisabled: true, // 並び替え機能を実装したら false にする
        order: "", // 並び替え機能を実装したら "asc" にする
        orderKey: "", // 並び替え機能を実装したら設定する
        key: "house_card",
        value: "house_card",
      });
      defaultTableHeaders.value.sort(function (a, b) {
        return a.id - b.id;
      });
    }

    const tableHeadersStr: string | null = localStorageGetPageHeaders(
      MEMBER_LIST_FILE_NAME
    );

    if (tableHeadersStr === null) {
      // localStorage に無ければ、デフォルト値を設定
      tableHeaders.value = JSON.parse(
        JSON.stringify(defaultTableHeaders.value)
      ) as TableHeader[];
    } else {
      // localStorage にあれば、それをを設定
      // Vuetify v3移行対応: 古いLocalStorageデータにtitle/keyがない場合はtext/valueから補完
      const parsedHeaders = JSON.parse(tableHeadersStr) as TableHeader[];
      tableHeaders.value = parsedHeaders.map((header) => ({
        ...header,
        title: header.title ?? header.text,
        key: header.key ?? header.value,
      }));
    }
  };

  /**
   * 表示するものだけにした 会員一覧 ヘッダー
   */
  const filteredTableHeaders = (): TableHeader[] => {
    return tableHeaders.value.filter((value) => value.display);
  };

  const getRowProps = (item: ListData) => {
    console.log(item);
    return {
      class: "bk_gray_1",
    };
  };

  /**
   * Loadingフラグ
   */
  const listLoading = ref(false);

  /**
   * listOptions
   */
  const listOptions = reactive<ListOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  });

  /**
   * 会員一覧 データ
   */
  const listDataArray = reactive<ListData[]>([]);

  /**
   * 会員一覧 絞込み後の全データ数
   */
  const listDataTotalCount = ref(0);

  /**
   * 会員一覧 ページ数
   */
  const pageCount = computed(() =>
    Math.max(1, Math.ceil(listDataTotalCount.value / (listOptions.itemsPerPage || 1)))
  );

  /**
   * 選択されたチェックボックス
   */
  const selectListCheckbox = ref<ListData[]>([]);

  //ページング範囲の表示
  const displayPagingRangeText = ref("");

  /**
   * 設定 @Click
   */
  const onClickListSetting = (): void => {
    // 会員一覧 ヘッダー設定用 に現在の状態をコピー
    tableHeadersSetting.value = JSON.parse(
      JSON.stringify(tableHeaders.value)
    ) as TableHeader[];
  };

  /**
   * 一覧設定ダイアログ キャンセル @Click
   */
  const onClickListSettingCancel = (): void => {
    // 会員一覧 ヘッダー設定用 クリア
    tableHeadersSetting.value = [];
  };

  /**
   * 一覧設定ダイアログ リセット @Click
   */
  const onClickListSettingReset = (): void => {
    // 会員一覧 ヘッダー設定用 デフォルト値を設定
    tableHeadersSetting.value = JSON.parse(
      JSON.stringify(defaultTableHeaders.value)
    ) as TableHeader[];
  };

  /**
   * 一覧設定ダイアログ 設定 @Click
   */
  const onClickListSettingOk = (): void => {
    // 会員一覧 ヘッダー へ設定を反映
    tableHeaders.value = tableHeadersSetting.value;

    // localStorage へ保存
    localStorageSetPageHeaders(
      MEMBER_LIST_FILE_NAME,
      JSON.stringify(tableHeadersSetting.value)
    );

    // 会員一覧 ヘッダー設定用 クリア
    tableHeadersSetting.value = [];
  };

  return {
    defaultTableHeaders,
    tableHeaders,
    tableHeadersSetting,
    initTableHeaders,
    filteredTableHeaders,
    getRowProps,
    listLoading,
    listOptions,
    listDataArray,
    listDataTotalCount,
    pageCount,
    selectListCheckbox,
    displayPagingRangeText,
    onClickListSetting,
    onClickListSettingCancel,
    onClickListSettingReset,
    onClickListSettingOk,
  };
};
